import { mapGetters, mapState } from 'vuex';
import ApiService from '@/services/apiService';
import NiotaClassicApiService from '@/services/niotaClassicApiService';
import get from 'lodash/get';

export default {
  data() {
    return {
      accountFilterReady: false,
      selectedAccounts: [],

      searchOperator: 'AND',
      searchItems: [],
      searchGroups: [],
      searchTags: [],
      searchZones: [],
      bridgeTypes: [],
      deviceTypes: [],
      deviceDrivers: [],
      loadingData: {
        deviceGroups: false,
        deviceTypes: false,
        deviceDrivers: false,
        tags: false,
        zones: false,
        bridgeTypes: false,
      },
    };
  },
  computed: {
    ...mapState('app', ['language']),
    ...mapGetters({ isFeatureFlagEnabled: 'app/isFeatureFlagEnabled' }),
    loadingDataDone() {
      return Object.values(this.loadingData).every((value) => value);
    },
    searchTypes() {
      const searchTypes = [
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.quickSearch'),
          param: 'quickSearch',
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.title'),
          param: 'title',
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.group'),
          param: 'group',
          items: [],
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.tag'),
          param: 'tag',
          items: [],
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.keyValue'),
          param: 'keyValue',
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props')['bridge.type'],
          param: 'bridge.type',
          items: [],
        },

        this.isFeatureFlagEnabled('env_dispatcher_devicetype_fallback')
          ? {
              text: this.$i18n_t('DigitalTwin.VirtualDevice.props.deviceType'),
              param: 'deviceType',
              items: [],
            }
          : {
              text: this.$i18n_t('DigitalTwin.VirtualDevice.props.deviceDriver'),
              param: 'deviceDriver',
              searchHandler: async (searchFor, page, pageSize) => {
                const result = await this.searchForDeviceDrivers(searchFor, page, pageSize);
                return {
                  totalCount: result.totalCount,
                  items: result.deviceDrivers,
                };
              },
            },

        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.deviceId'),
          param: 'deviceId',
        },
        {
          text: this.$i18n_t('Common.id'),
          param: 'id',
        },
        {
          text: this.$i18n_t('Common.digitalTwins.searchParentLabel'),
          param: 'parentId',
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.operationalStatus'),
          param: 'operationalStatus',
          items: [],
          combobox: true,
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.health'),
          param: 'health',
          items: [],
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.zone'),
          param: 'zone',
          items: [],
        },
        {
          text: this.$i18n_t('DigitalTwin.VirtualDevice.props.address'),
          param: 'address',
        },
      ];

      return searchTypes;
    },
    statisticsSearchTypes() {
      return this.searchTypes.filter((st) => st.param !== 'zone');
    },
    operationalStatus() {
      return {
        0: this.$i18n_t('NiotaClassic.Devices.form.unset'),
        1: this.$i18n_t('NiotaClassic.Devices.form.inTransit'),
        2: this.$i18n_t('NiotaClassic.Devices.form.readyForOperation'),
        3: this.$i18n_t('NiotaClassic.Devices.form.inOperation'),
        4: this.$i18n_t('NiotaClassic.Devices.form.temporaryInactive'),
        5: this.$i18n_t('NiotaClassic.Devices.form.decommissioned'),
      };
    },
    operationalStatusItems() {
      return Object.keys(this.operationalStatus).map((status) => {
        return { text: this.operationalStatus[status], value: status };
      });
    },
  },
  watch: {
    language() {
      (this.searchTypes.find((item) => item.param === 'operationalStatus') || {}).items = this.operationalStatusItems;
      (this.searchTypes.find((item) => item.param === 'health') || {}).items = this.healthStatusItems;
      (this.searchTypes.find((item) => item.param === 'group') || {}).items = this.searchGroups;
      (this.searchTypes.find((item) => item.param === 'tag') || {}).items = this.searchTags;
      (this.searchTypes.find((item) => item.param === 'zone') || {}).items = this.searchZones;
      if (this.isFeatureFlagEnabled('env_dispatcher_devicetype_fallback')) {
        (this.searchTypes.find((item) => item.param === 'deviceType') || {}).items = this.deviceTypes;
      }
      (this.searchTypes.find((item) => item.param === 'deviceDriver') || {}).items = this.deviceDrivers;
      (this.searchTypes.find((item) => item.param === 'bridge.type') || {}).items = this.bridgeTypes;
    },
  },
  mounted() {
    this.getMultiFilterData();
  },
  methods: {
    fetchDeviceGroups() {
      ApiService.virtualDeviceFetchGroups(this, null) // fetch groups for all accounts (hopefully only those the user has access to)
        .then((result) => {
          this.searchGroups = result.groupList;
          (this.searchTypes.find((item) => item.param === 'group') || []).items = result.groupList;
          this.loadingData.deviceGroups = true;
        })
        .catch((error) => {
          console.error(error);
        });
    },
    resetLoadingData() {
      for (const key in this.loadingData) {
        this.loadingData[key] = false;
      }
    },
    getMultiFilterData() {
      this.resetLoadingData();

      (this.searchTypes.find((item) => item.param === 'operationalStatus') || {}).items = this.operationalStatusItems;
      (this.searchTypes.find((item) => item.param === 'health') || {}).items = this.healthStatusItems;

      this.fetchDeviceGroups();

      if (this.isFeatureFlagEnabled('env_dispatcher_devicetype_fallback')) {
        NiotaClassicApiService.deviceTypesGetPlain()
          .then((res) => {
            this.deviceTypes = res.data.devicetypes.map((dt) => {
              return { value: dt.id, text: dt.title, id: dt.id };
            });

            (this.searchTypes.find((item) => item.param === 'deviceType') || {}).items = this.deviceTypes;

            this.loadingData.deviceTypes = true;
          })
          .catch((error) => {
            this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.snacks.fetchDeviceTypesFailed'), 'error');
            console.error(error);
          });
      }

      ApiService.virtualDeviceFetchTagsAndZones()
        .then((result) => {
          this.searchTags = result.tagList;
          this.searchZones = result.zoneList;
          (this.searchTypes.find((item) => item.param === 'tag') || []).items = result.tagList;
          (this.searchTypes.find((item) => item.param === 'zone') || []).items = result.zoneList;

          this.loadingData.tags = true;
          this.loadingData.zones = true;
        })
        .catch((error) => {
          console.error(error);
        });

      ApiService.getBridgeTypes()
        .then((bridgeTypes) => {
          this.bridgeTypes = bridgeTypes
            .filter((bridge) => {
              // this bridge-type must have a create-functionality
              // otherwise it should have a read to get 1 single existing device as import
              if (
                get(bridge, 'manifest.functionScope.extension.devices.create') ||
                get(bridge, 'manifest.functionScope.extension.devices.read')
              ) {
                return true;
              }
              return false;
            })
            .map((bridge) => bridge.manifest.displayName);

          (this.searchTypes.find((item) => item.param === 'bridge.type') || {}).items = this.bridgeTypes;

          this.loadingData.bridgeTypes = true;
        })
        .catch((error) => {
          console.error(error);
        });
    },
    async searchForDeviceDrivers(search, page, pageSize) {
      const result = await ApiService.virtualDeviceDrivers.fetch({
        fields: 'id,title',
        offset: page * pageSize,
        limit: pageSize,
        search,
        sortBy: 'title',
      });
      result.deviceDrivers = result.deviceDrivers.map((dt) => {
        return { value: dt.id, text: dt.title, id: dt.id };
      });
      return result;
    },
  },
};
